import React from 'react';
import { ThumbsUp, Heart, PartyPopper, Lightbulb, X } from 'lucide-react';
import { TimelineReaction } from '../../models/TimelineReaction';
import { getDisplayName } from '../../utils/userUtils';
import { User } from '../../models/User';

// Get icon for reaction type
export const getReactionIcon = (type: 'like' | 'love' | 'celebrate' | 'insightful' | null, size = 16) => {
  switch (type) {
    case 'like':
      return <ThumbsUp size={size} className="text-blue-600" />;
    case 'love':
      return <Heart size={size} className="text-red-600" />;
    case 'celebrate':
      return <PartyPopper size={size} className="text-yellow-600" />;
    case 'insightful':
      return <Lightbulb size={size} className="text-purple-600" />;
    default:
      return <ThumbsUp size={size} className="text-gray-400" />;
  }
};

interface ReactionButtonProps {
  type: 'like' | 'love' | 'celebrate' | 'insightful' | 'support';
  onClick: () => void;
  count?: number;
  isActive?: boolean;
}

export const ReactionButton: React.FC<ReactionButtonProps> = ({ type, onClick, count, isActive }) => {
  const getLabel = () => {
    switch (type) {
      case 'like': return 'Like';
      case 'love': return 'Love';
      case 'celebrate': return 'Applaud';
      case 'insightful': return 'Insightful';
      case 'support': return 'Support';
    }
  };

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center px-2 py-1 rounded-md text-sm font-medium transition-colors ${
        isActive 
          ? 'bg-blue-100 text-blue-700 border border-blue-200' 
          : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100'
      }`}
    >
      {getReactionIcon(type, 16)}
      <span className="ml-1">{getLabel()}</span>
      {count !== undefined && count > 0 && (
        <span className="ml-1">({count})</span>
      )}
    </button>
  );
};

interface ReactionSummaryProps {
  reactions: TimelineReaction[];
  getReactionType: (r: TimelineReaction) => 'like' | 'love' | 'celebrate' | 'insightful' | 'support';
  onShowDetails: () => void;
}

export const ReactionSummary: React.FC<ReactionSummaryProps> = ({ reactions, getReactionType, onShowDetails }) => {
  const counts = {
    like: 0,
    love: 0,
    celebrate: 0,
    insightful: 0,
    support: 0,
    total: reactions.length
  };

  reactions.forEach(reaction => {
    const type = getReactionType(reaction);
    if (counts.hasOwnProperty(type)) {
      counts[type as keyof typeof counts]++;
    }
  });

  if (reactions.length === 0) return null;

  return (
    <button
      onClick={onShowDetails}
      className="inline-flex items-center text-sm text-gray-600 hover:text-gray-700 hover:bg-gray-50 px-2 py-1 rounded-md transition-colors"
    >
      <span className="text-xs font-bold mr-2">
        {reactions.length} {reactions.length === 1 ? 'reaction' : 'reactions'}
      </span>
      <div className="flex items-center space-x-1">
        {counts.like > 0 && (
          <div className="flex items-center">
            {getReactionIcon('like', 14)}
            <span className="ml-1 text-xs">{counts.like}</span>
          </div>
        )}
        {counts.love > 0 && (
          <div className="flex items-center">
            {getReactionIcon('love', 14)}
            <span className="ml-1 text-xs">{counts.love}</span>
          </div>
        )}
        {counts.celebrate > 0 && (
          <div className="flex items-center">
            {getReactionIcon('celebrate', 14)}
            <span className="ml-1 text-xs">{counts.celebrate}</span>
          </div>
        )}
        {counts.insightful > 0 && (
          <div className="flex items-center">
            {getReactionIcon('insightful', 14)}
            <span className="ml-1 text-xs">{counts.insightful}</span>
          </div>
        )}
        {counts.support > 0 && (
          <div className="flex items-center">
            {getReactionIcon('support', 14)}
            <span className="ml-1 text-xs">{counts.support}</span>
          </div>
        )}
      </div>
    </button>
  );
};

interface ReactionPickerProps {
  onReact: (type: 'like' | 'love' | 'celebrate' | 'insightful' | 'support') => void;
  onClose: () => void;
}

export const ReactionPicker: React.FC<ReactionPickerProps> = ({ onReact, onClose }) => {
  return (
    <div className="absolute bottom-full mb-2 left-0 bg-white border border-gray-200 rounded-lg shadow-lg p-2 flex space-x-2 z-10">
      <button
        onClick={() => onReact('like')}
        className="p-2 hover:bg-gray-100 rounded-md transition-colors"
        title="Like"
      >
        {getReactionIcon('like', 20)}
      </button>
      <button
        onClick={() => onReact('love')}
        className="p-2 hover:bg-gray-100 rounded-md transition-colors"
        title="Love"
      >
        {getReactionIcon('love', 20)}
      </button>
      <button
        onClick={() => onReact('celebrate')}
        className="p-2 hover:bg-gray-100 rounded-md transition-colors"
        title="Applaud"
      >
        {getReactionIcon('celebrate', 20)}
      </button>
      <button
        onClick={() => onReact('insightful')}
        className="p-2 hover:bg-gray-100 rounded-md transition-colors"
        title="Insightful"
      >
        {getReactionIcon('insightful', 20)}
      </button>
    </div>
  );
};

interface ReactionsListModalProps<T> {
  reactions: T[];
  getReactionType: (r: T) => 'like' | 'love' | 'celebrate' | 'insightful' | 'support';
  onClose: () => void;
  title?: string;
  users: User[];
}

export function ReactionsListModal<T extends { userId: string }>({ reactions, getReactionType, onClose, title = "Reactions", users = [] }: ReactionsListModalProps<T>) {
  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-96 overflow-hidden">
        <div className="flex justify-between items-center px-4 py-3 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          <div className="mb-4">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              All ({reactions.length})
            </span>
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {reactions.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No reactions yet</div>
            ) : (
              reactions.map((reaction, index) => {
                const user = (users || []).find(u => u.id === reaction.userId);
                return (
                  <div key={index} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-md">
                    <div className="flex-shrink-0">
                      {getReactionIcon(getReactionType(reaction), 16)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {user ? getDisplayName(user) : `User ID: ${reaction.userId}`}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 